import React, { useState } from 'react';
import styled from 'styled-components';
import TextComponent from '@/components/TextComponent';
import Card from '@/components/Card';
import LabelContent from '@/components/LabelContent';
import { formatTelDefault } from '@/utils/form';
import { ReactComponent as LogoutIcon } from '@/assets/images/common/logout.svg';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { authState } from '@/recoil/auth';
import { pharmacyState } from '@/recoil/pharmacy';
import usePharmacyQuery from '@/hooks/pharmacy/usePharmacyQuery';
import useModal from '@/hooks/common/useModal';
import DutyTimeModal from './containers/DutyTimeModal';
import { formatDutyTime, FormattedDutyType } from '@/utils/time';
import { checkDomain } from '@/utils/domain';
import { deleteCookie, getCookie } from '@/utils/cookie';
import { serviceTokenMap } from '@/constants/cookie';
import ContactUpdateModal from './containers/ContactUpdateModal';
import pharmacyServices from '@/services/phamacy';
import { createContactToken, verifyContactToken } from '@/apis/pharmacy';
import { ContactService } from '@/services/phamacy/interface';
import GlobalAlert from '@/components/GlobalAlert';
import { getErrorMessage } from '@/utils/error';
import theme from '@/styles/theme';

type ContactInputs = {
  target: string;
  token: string;
};

const Mypage = () => {
  const { pharmacyDetailQuery, pharmacyContactsUpdateMutation } =
    usePharmacyQuery();
  const { data: pharmacyData } = pharmacyDetailQuery();
  const pharmacyDetail = pharmacyData?.getPharmacy;
  const dutyTime = formatDutyTime(pharmacyDetail?.pharmacyDuties);
  const [DutyModal, setDutyModal] = useModal(false);
  const [ContactModal, setContactModal] = useModal(false);
  const { token } = useRecoilValue(authState);
  const resetAuth = useResetRecoilState(authState);
  const resetPharmacy = useResetRecoilState(pharmacyState);
  const contacts = pharmacyServices.getPharmacyContactsService();
  const [errorContacts, setErrorContacts] = useState('');

  const pharmacyContacts = contacts
    ?.filter(contact => contact.phone)
    .map(contact => (
      <PhoneNumber>{formatTelDefault(contact.phone)}</PhoneNumber>
    ));

  const handleSendToken = async ({ target }: Pick<ContactInputs, 'target'>) => {
    return createContactToken(token, target);
  };

  const handleApproveToken = async ({
    target,
    token: contactToken
  }: ContactInputs) => {
    return verifyContactToken(token, target, contactToken);
  };

  const handleInitErrorText = () => setErrorContacts('');

  const handleSubmitContacts = async (contacts: ContactService) => {
    const activedContacts = contacts.reduce((acc, contact) => {
      const { isSaved, ...rest } = contact;
      return isSaved ? [...acc, rest] : acc;
    }, [] as ContactService);
    try {
      if (activedContacts.length === 0) {
        return setErrorContacts('하나 이상의 번호를 입력해주세요');
      }

      const response = await pharmacyContactsUpdateMutation.mutateAsync(
        activedContacts
      );

      if (!response) return;

      response.updatePharmacyContacts && setContactModal(false);
      setErrorContacts('');
    } catch (error) {
      GlobalAlert.show({
        title: getErrorMessage(error)
      });
    }
  };

  const logout = () => {
    resetAuth();
    resetPharmacy();

    const serviceType = checkDomain();
    const token = getCookie(serviceTokenMap[serviceType]);

    if (!token) return;

    // 쿠키에 토큰이 존재할 시에 토큰 쿠키 폐기
    deleteCookie(serviceTokenMap[serviceType]);
  };

  const onDutyModal = () => {
    setDutyModal(true);
  };
  const onContactModal = () => {
    setContactModal(true);
  };

  return (
    <Wrapper>
      <Body>
        <Card>
          <MainInfo>
            <MainInfoTop>
              <TextComponent
                fontSize={24}
                fontWeight={700}
                style={{ marginBottom: 7 }}>
                {pharmacyDetail?.name}
              </TextComponent>
              <TextComponent fontSize={20} fontWeight={500} color={theme.DF_COLOR.GRAY600}>
                {pharmacyDetail?.accountInfo.username}
              </TextComponent>
            </MainInfoTop>
            <Division marginVertical={'2.5rem'} />
            <MainInfoBody>
              <MainInfoBodyContent>
                <LabelContent
                  label={'약사'}
                  content={pharmacyDetail?.pharmacist?.pharmacistName}
                />
                <LabelContent
                  label={'전화번호'}
                  content={formatTelDefault(pharmacyDetail?.tel ?? '')}
                />
                <LabelContent
                  label={'팩스번호'}
                  content={pharmacyDetail?.faxNumber ?? ''}
                />
                <LabelContent
                  label={'정산은행'}
                  content={pharmacyDetail?.pharmacist?.bank ?? ''}
                />
                <LabelContent
                  label={'계좌번호'}
                  content={pharmacyDetail?.pharmacist?.accountNumber ?? ''}
                />
              </MainInfoBodyContent>
              <Logout onClick={() => logout()}>
                <LogoutIcon />
                <TextComponent fontWeight={600} color={theme.DF_COLOR.GRAY600}>
                  로그아웃
                </TextComponent>
              </Logout>
            </MainInfoBody>
          </MainInfo>
        </Card>

        <Card>
          <DetailInfo>
            <DetailInfoTop>
              <LabelContent
                label={'약국주소'}
                content={`${pharmacyDetail?.roadAddress}, ${pharmacyDetail?.detailAddress}`}
              />
              <LabelContent
                label={'알림톡 수신 번호'}
                edit={onContactModal}
                content={pharmacyContacts}
              />
              <DutyWrapper>
                <LabelContent
                  label={'영업시간'}
                  edit={dutyTime ? onDutyModal : undefined}
                />
                <DutyTime>
                  {dutyTime?.map((time: FormattedDutyType, key: number) => {
                    return (
                      <DutyTimeItem key={key} isClosed={time.isClosed}>
                        <DayText weekDay={time.weekDay}>{time.day}</DayText>
                        <TimeText>{`${time.openTime} ~ ${time.closeTime}`}</TimeText>
                      </DutyTimeItem>
                    );
                  })}
                </DutyTime>
              </DutyWrapper>
            </DetailInfoTop>
            <Division marginVertical={'3.8rem'} />
            <DetailInfoBottom>
              <TextComponent color={theme.DF_COLOR.GRAY600}>
                대표번호, 팩스번호, 정산계좌, 약국주소 변경이 필요한 경우 {'\n'}
                고객센터로 연락해주시기 바랍니다.
              </TextComponent>
            </DetailInfoBottom>
          </DetailInfo>
        </Card>
      </Body>
      <ContactModal
        header={'알림톡 수신 번호'}
        clickOverlayToClose={false}
        onClickClose={handleInitErrorText}>
        <ContactUpdateModal
          contactList={contacts}
          onSend={handleSendToken}
          onApprove={handleApproveToken}
          onSubmit={handleSubmitContacts}
          errorContacts={errorContacts}
        />
      </ContactModal>
      {dutyTime && (
        <DutyModal header={' '} clickOverlayToClose={false}>
          <DutyTimeModal dutyTime={dutyTime} setDutyModal={setDutyModal} />
        </DutyModal>
      )}
    </Wrapper>
  );
};

export default Mypage;

const Wrapper = styled.div`
  width: 100%;
  ${({ theme }) => theme.media.threeQuarter`
    width: 100%;
  `};
`;

const Body = styled.div`
  display: flex;

  > * {
    &:nth-child(1) {
      margin-right: 1.3rem;
      flex: 3;
    }

    &:nth-child(2) {
      flex: 7;
    }
  }
`;

const MainInfo = styled.div`
  display: flex;
  height: 100%;
  flex-flow: column;
  padding: 2.5rem 2.8rem;
`;

const MainInfoTop = styled.div`
  text-align: center;
`;
const MainInfoBody = styled.div`
  align-self: center;
  width: 80%;
  display: flex;
  flex-flow: column;
  height: 100%;
  justify-content: space-between;
`;

const MainInfoBodyContent = styled.div`
  > * {
    margin-bottom: 33px;
  }
`;
const Logout = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  > * {
    &:first-child {
      margin-right: 5px;
    }
  }
`;

const DetailInfo = styled.div`
  padding: 4rem 2.8rem;
`;

const DetailInfoTop = styled.div`
  > * {
    margin-bottom: 33px;
  }
`;

const DetailInfoBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: end;
`;

const Division = styled.div<{ marginVertical: string }>`
  width: 100%;
  height: 1px;
  margin: ${({ marginVertical }) => `${marginVertical} 0`};
  background: #d8d8d8;
`;

const DutyWrapper = styled.div``;

const DutyTime = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: 3px;
  width: 100%;

  > * {
    &:nth-child(odd) {
      margin-right: 3rem;
    }
  }
`;

const DutyTimeItem = styled.div<{ isClosed: boolean }>`
  display: flex;
  width: 45%;
  flex-flow: row;
  margin-bottom: 1rem;
  ${({ isClosed }) => isClosed && 'opacity: 0.2'}
`;

const DayText = styled(TextComponent)<{ weekDay: number }>`
  color: ${({ weekDay }) => (weekDay === 6 ? 'blue' : weekDay > 6 && 'red')};
  font-weight: 400;
  font-size: 20px;
  margin-right: 1rem;
  flex: 2.5;
`;

const TimeText = styled(TextComponent)`
  font-weight: 400;
  font-size: 20px;
  flex: 7.5;
`;

const PhoneNumber = styled.span`
  display: inline-block;
  margin-right: 24px;
`;
